import { Pressable, ScrollView, StyleSheet, useWindowDimensions } from 'react-native';
import { View } from '@/components/Themed';
import { BasicScrollView } from "@/components/BasicScrollView";
import Head from 'expo-router/head';
import * as AppUtils from '@/utilities/AppUtils';
import * as AppUtilities from '@/utilities/AppUtilities';
import * as AppConstants from '@/utilities/AppConstants';
import * as AppConsts from '@/utilities/AppConsts';
import { CustomText, CustomTextStyles } from '@/components/CustomText';
import type { RootState } from '@/state/store';
import { useSelector, useDispatch } from 'react-redux';
import { AppText } from '@/components/AppText';
import { Image } from 'expo-image';
import Animated, { FadeInDown, FadeInRight } from "react-native-reanimated";
import { Link, RelativePathString, router, useRouter } from 'expo-router';
import Badge from '@/components/Badge';
import NewImage from '@/components/NewImage';
import { Match } from '@/types/match';
import { Meta } from '@/components/Meta';

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 20,
    alignItems: 'center',
    backgroundColor: 'transparent',
    gap: 20,
  },
  noMatches: {
    width: '100%',
    alignItems: 'center',
    backgroundColor: 'transparent',
    paddingTop: 50,
  },
});

const ROWS_VERTICAL_GAP_BETWEEN = 20;

export default function Matches() {
  const matches = useSelector((state: RootState) => state.user.matches);
  const router = useRouter();
  const { height, width } = useWindowDimensions();

  if (AppUtilities.DEBUGLEVEL.dev) {
    AppUtilities.info("Matches render matches:");
    AppUtilities.dir(matches);
  }

  const scrollViewPadding = AppUtils.isSmallScreen(width) ? 10 : 20;
  const avatarSize = AppUtils.isSmallScreen(width) ? 32 : 80;
  const avatarBorderRadius = Math.floor(avatarSize);
  const rowPadding = AppUtils.isSmallScreen(width) ? 10 : 20;
  const rowGapBetweenComponents = AppUtils.isSmallScreen(width) ? 10 : 15;
  const newImageOrBadgeWidth = 32;
  const rowTextHeaderStyle = AppUtils.isSmallScreen(width) ? CustomTextStyles.medium : CustomTextStyles.default;

  const renderRow = (match : Match) => {
    const hasNewImageOrBadge = match.isnew || (match.unreadcount ?? 0) > 0;
    const rowTextWidth = width - (scrollViewPadding * 2) - (rowPadding * 2) - (rowGapBetweenComponents * (hasNewImageOrBadge ? 3 : 2)) - avatarSize - (hasNewImageOrBadge ? newImageOrBadgeWidth : 0);
    return (
      <Link
        href={("/chat/" + match.uniqueid) as RelativePathString} asChild={true}
        key={match.uniqueid}
      >
        <Pressable
          style={{
            backgroundColor: "white",
            flexDirection: "row",
            gap: rowGapBetweenComponents,
            width: "100%",
            borderRadius: 20,
            alignItems: "center",
            padding: rowPadding 
          }}
        >
          <Image
            source={
              match.member ?
                  { uri: AppUtils.createBackendLink('/rabbl/api/visual?thumbnail=yes&uniqueid=' + match.member?.profile.visuals[0].uniqueid) }
                :
                  require('@/assets/images/PixelarticonsUser80x80.png')
            }
            style={{
              width: avatarSize,
              height: avatarSize,
              borderRadius: avatarBorderRadius,
            }}
            contentFit='contain'
          />
          <View style={{ backgroundColor: "transparent", gap: 3, width: rowTextWidth }}>
            <CustomText
              numberOfLines={1}
              ellipsizeMode="tail"
              style={rowTextHeaderStyle}
            >
              {match.member?.profile.name ?? AppConsts.DELETED_USER}
            </CustomText>
            <AppText numberOfLines={1} ellipsizeMode="tail">
              { match.member ?
                    AppUtils.getMemberAge(match.member) + " - " + AppUtils.getMemberShortLocation(match.member?.user_info)
                  :
                    "Last updated: " + new Date(match.updated_at).toLocaleDateString(undefined, { dateStyle: "short" })
              }
            </AppText>
          </View>
          {
            hasNewImageOrBadge && (
              <View style={{ flexDirection: "row", width: newImageOrBadgeWidth, justifyContent: "center" }}>
                {
                  match.isnew ?
                      <NewImage id={5} />
                    :
                      <Badge val={match.unreadcount ?? 0} />
                }
              </View>
            )
          }
        </Pressable>
      </Link>
    );
  };

  return (
    <BasicScrollView center={false} contentContainerStyle={{ marginHorizontal: scrollViewPadding }}>
      <Meta title={"Matches"} />
      { matches.length == 0 && (
        <View style={styles.noMatches}>
          <CustomText>No matches yet</CustomText>
        </View>
      )}
      <View style={{ width: "100%", gap: ROWS_VERTICAL_GAP_BETWEEN, backgroundColor: "transparent" }}>
        {matches.map((match, index) => renderRow(match))}
      </View>
    </BasicScrollView>
  );
}
